import { useEffect, useState } from "react";

import { Colors } from "@blueprintjs/core";
import { useLocation, useNavigate } from "react-router";

import { isWorkspaceAdmin } from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import { useAuth } from "@/components/lib/auth";
import { Container } from "@/components/pieces/bootstrap";
import { PracticeSvg } from "@/components/pieces/icons/svg/practice";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BRAND_PURPLE, HEADING_FONT, SUBTEXT_COLOR } from "@/css/constants";
import { Tab } from "@/types/tabs";

const ROLEPLAY_TABS = [
  {
    key: "/roleplays",
    label: "Dashboard",
  },
  {
    key: "/roleplays/collections",
    label: "Collections",
  },
  {
    key: "/roleplays/scenarios",
    label: "Scenarios",
  },
];

const WORKSPACE_ADMIN_ADDITIONAL_TABS = [
  {
    key: "/roleplays/assignments",
    label: "Assignments",
  },
  {
    key: "/roleplays/analytics",
    label: "Analytics",
  },
];

export function RoleplaysHeader({ showSubTitle = true }) {
  const { currentWorkspace } = useAuth();
  const isAdmin = isWorkspaceAdmin();
  const tabs = [
    ...ROLEPLAY_TABS,
    ...(!currentWorkspace.isPersonal && isAdmin
      ? WORKSPACE_ADMIN_ADDITIONAL_TABS
      : []),
  ];
  const [selectedTab, setSelectedTab] = useState<Tab>(ROLEPLAY_TABS[0]);
  const location = useLocation();

  const navigate = useNavigate();

  useEffect(() => {
    const path = location.pathname;
    const tab = tabs.find((tab) => tab.key === path);
    if (tab) {
      setSelectedTab(tab);
    }
  }, [location]);

  return (
    <Box width={1} className="bbs" pt={4} bg={Colors.WHITE}>
      <Container>
        <div className="flex-1 mb-6">
          <Flex alignItems="center">
            <PracticeSvg size={26} color={BRAND_PURPLE} />
            <Box
              ml={3}
              fontFamily={HEADING_FONT}
              className="justify-start text-gray-800 text-3xl font-bold leading-10"
            >
              Roleplays
            </Box>
          </Flex>
          {showSubTitle && (
            <Box color={SUBTEXT_COLOR} mt={2}>
              Practice and improve your communication skills through AI-powered
              scenarios
            </Box>
          )}
        </div>
        <Tabs
          className="overflow-x-auto hide-scroll-bar"
          defaultValue={selectedTab.key}
          value={selectedTab.key}
          onValueChange={(value: string) => {
            const tab = tabs.find((tab) => tab.key === value);
            if (tab) {
              setSelectedTab(tab);
              navigate(tab.key);
            }
          }}
        >
          <TabsList className="flex h-fit px-0 gap-8 justify-start">
            {ROLEPLAY_TABS.map((tab) => (
              <TabsTrigger className="pb-5 px-0" key={tab.key} value={tab.key}>
                {tab.label}
              </TabsTrigger>
            ))}

            {!currentWorkspace.isPersonal && isAdmin && (
              <>
                <div className="border-l border-gray-300 h-4 mx-2 mb-4" />
                {WORKSPACE_ADMIN_ADDITIONAL_TABS.map((tab) => (
                  <TabsTrigger className="pb-5" key={tab.key} value={tab.key}>
                    {tab.label}
                  </TabsTrigger>
                ))}
              </>
            )}
          </TabsList>
        </Tabs>
      </Container>
    </Box>
  );
}
